// Isolated API client for the VOYA9 affiliate surface.
// Keeps affiliate state entirely separate from the consumer auth stack
// (different token key, different helpers) so the same browser can be
// signed into both simultaneously without conflict.

const BACKEND_URL = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL;
export const AFF_API_BASE = `${BACKEND_URL}/api`;

const AFF_TOKEN_KEY = "vy9_affiliate_token";
const AFF_COOKIE_NAME = "vy9_aff";

export function getAffiliateToken() {
  return localStorage.getItem(AFF_TOKEN_KEY);
}
export function setAffiliateToken(token) {
  if (token) localStorage.setItem(AFF_TOKEN_KEY, token);
}
export function clearAffiliateToken() {
  localStorage.removeItem(AFF_TOKEN_KEY);
}

// Reads the vy9_aff cookie that the backend sets during ?aff=... tracking.
// Returns the click ID (string) or null.
export function readAffiliateCookie() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${AFF_COOKIE_NAME}=`));
  if (!match) return null;
  return decodeURIComponent(match.split("=")[1] || "") || null;
}

export class AffiliateApiError extends Error {
  constructor(status, detail, raw) {
    super(detail || `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
    this.raw = raw;
  }
}

export async function affiliateApi(path, { method = "GET", body, headers = {}, auth = false, credentials } = {}) {
  const token = auth ? getAffiliateToken() : null;
  const url = `${AFF_API_BASE}${path}`;
  // We use XMLHttpRequest instead of fetch here on purpose. The Emergent
  // preview platform injects a `window.fetch` wrapper that reads the
  // response body for its own logging before returning the Response
  // object — which leaves our `res.text()` (and `res.clone().text()`)
  // calls staring at an already-consumed stream. The result is a
  // useless empty body on every error, and toasts that just say
  // "Request failed (NNN)" without the BE's validation detail. XHR
  // bypasses that wrapper entirely, so we always see the real body.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    // `credentials: "include"` on fetch maps to `withCredentials = true`
    // on XHR — required for the /track cookie hand-off, otherwise opt out.
    xhr.withCredentials = credentials === "include" || (auth === true && credentials !== "omit");

    xhr.onload = () => {
      const text = xhr.responseText || "";
      let data = null;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
        return;
      }
      // FastAPI returns `detail` as either a string (HTTPException) or
      // an array of `{loc, msg, type}` (Pydantic validation). Normalize
      // both into a readable single-line string so the toast actually
      // tells the member which field is wrong.
      let detail = data && data.detail;
      if (Array.isArray(detail)) {
        detail = detail.map((iss) => {
          if (typeof iss === "string") return iss;
          const field = Array.isArray(iss?.loc) ? iss.loc.filter((p) => p !== "body").join(".") : "";
          const msg = iss?.msg || iss?.message || "Invalid value";
          return field ? `${field}: ${msg}` : msg;
        }).filter(Boolean).join(" · ");
      }
      if (typeof detail !== "string" || !detail) {
        detail = `Request failed (${xhr.status})`;
      }
      // eslint-disable-next-line no-console
      console.warn("[affiliateApi] error", { status: xhr.status, path, body: data || text });
      reject(new AffiliateApiError(xhr.status, detail, data));
    };
    xhr.onerror = () => {
      reject(new AffiliateApiError(0, "Network error — please check your connection.", null));
    };
    xhr.ontimeout = () => {
      reject(new AffiliateApiError(0, "Request timed out — please try again.", null));
    };
    xhr.send(body !== undefined ? JSON.stringify(body) : null);
  });
}

export const affGet = (p, opts) => affiliateApi(p, { ...opts, method: "GET" });
export const affPost = (p, body, opts) => affiliateApi(p, { ...opts, method: "POST", body });
export const affPut = (p, body, opts) => affiliateApi(p, { ...opts, method: "PUT", body });
export const affDelete = (p, opts) => affiliateApi(p, { ...opts, method: "DELETE" });

// Fire-and-forget click tracking. Always called with credentials: "include"
// so the backend can set the vy9_aff cookie via Set-Cookie response header.
export function trackAffiliateClick(params) {
  const qs = new URLSearchParams(params).toString();
  return fetch(`${AFF_API_BASE}/affiliate/track?${qs}`, {
    method: "GET",
    credentials: "include",
  }).catch(() => {
    // Silent fail — tracking must never block UX.
  });
}
